import { useSwitchTeam } from "hooks/useSwitchTeam";
import { useRef, useState, useEffect } from "react";
import { useIntl } from "react-intl";

import {
  getNewChatTopic,
  AddHistoryMessage,
  setAdminAccountDeleted,
  logoutDispatch,
  IGenerateImageBody,
  setRememberChatTopicLoad
} from "redux/actions";
import { useSelector } from "redux/hooks";
import { useAppNotification } from "../AppNotification";

// Import the EventType enum and handlers
import { EventType, isValidEventType } from "../ChatServices/EventTypes";
import * as EventHandlers from "../ChatServices/EventHandler";

export const useImageGenerationStream = () => {
  const userDetail = useSelector((state) => state.authReducer.userDetail);
  const addHistory = useSelector((state) => state.chatReducer.addHistory);
  const { formatMessage } = useIntl();

  // Ref to hold the latest value of addHistory
  const addHistoryRef = useRef(addHistory);

  const switchTeam = useSwitchTeam();
  const { triggerNotification } = useAppNotification();

  useEffect(() => {
    // Update the ref whenever addHistory changes
    addHistoryRef.current = addHistory;
  }, [addHistory]);

  let controllerRef = useRef<AbortController | null>(null);
  const [done, setDone] = useState<boolean | null>(null);
  let chatId = useRef<number>(0);
  
  // Function to process event-based stream data using enum and switch
  const processEventStream = (
    eventData: string, 
    data: IGenerateImageBody, 
    admin_team_deleted: boolean,
    isPrivateChat: { current: boolean }
  ) => {
    // Split the received data into event chunks
    const events = eventData.split('event:').filter(event => event.trim());
    
    for (const event of events) {
      const parts = event.trim().split('data:');
      if (parts.length < 2) continue;
      
      // const eventTypeStr = parts[0].trim();
      // const eventContent = parts[1].trim();
          
    const eventTypeStr = parts[0].trim();
    
    // Get the full content after 'data:' by joining with 'data:'
    // This ensures we capture the entire data payload even if it contains colons
    const dataIndex = event.indexOf('data:');
    const eventContent = dataIndex >= 0 ? 
      event.substring(dataIndex + 5).trim() : 
      parts[1].trim();
      
      // Check if it's a valid event type
      if (!isValidEventType(eventTypeStr)) {
        continue;
      }
      
      // Cast to our enum type now that we've validated it
      const eventType = eventTypeStr as EventType;
      
      // Use a switch statement with the enum for type-safe handling
      switch (eventType) {
        case EventType.FILES_UPLOADED:
          EventHandlers.handleFilesUploaded(eventContent);
          break;
          
        case EventType.CHAT_UPDATED:
          EventHandlers.handleChatUpdated(eventContent, isPrivateChat, chatId);
          break;
          
        case EventType.ASSISTANT_MESSAGE_CREATED:
          EventHandlers.handleAssistantMessageCreated(eventContent, data);
          break;
          
        case EventType.CREDITS_UPDATED:
          EventHandlers.handleCreditsUpdated(eventContent, userDetail?? null);
          break;
        
        case EventType.ERROR:
          EventHandlers.handleErrorMessage(eventContent, data);
          break;
          
        // case EventType.IN_PROGRESS:
        //   EventHandlers.handleInProgress(eventContent, data);
        //   break;

        case EventType.GENERATION_STARTED:
          EventHandlers.handleImgInProgress(eventContent, data);
          break;
          
        case EventType.USER_MESSAGE_CREATED:
          EventHandlers.handleUserMessageCreated(eventContent, data);
          break;
            
        case EventType.MESSAGE:
          EventHandlers.handleImageMessage(eventContent, data, admin_team_deleted);
          break;

        case EventType.CHAT_STARTED:
          EventHandlers.handleChatStarted(eventContent);
          break;

        case EventType.IMAGES_GENERATED:
          EventHandlers.handleImagesGenerated(eventContent, data, admin_team_deleted);
          break;

        default:
          console.log('Unhandled event type:', eventType, eventContent);
      }
    }
  };

  const generateImage = ({
        messageViewType,
        ...data
      }: IGenerateImageBody) => {
    return new Promise(async (resolve, reject) => {
      try {
        controllerRef.current = new AbortController();

          // Add a default messageViewType if it's not provided
      const completeData: IGenerateImageBody = {
        ...data,
        messageViewType: messageViewType || "new"
      };

        const url = new URL(`${process.env.REACT_APP_API_BASEURL}/api/image/generate`);

        // Append team_id as a query parameter
        url.searchParams.append("team_id", userDetail?.user?.team?.id.toString() ?? "");

        const response = await fetch(url,
          {
            method: "POST",
            headers: {
              Accept: "text/event-stream",
              "Content-Type": "application/json",
              Authorization: `Bearer ${userDetail?.token}`,
            },
            body: JSON.stringify(data),
            signal: controllerRef?.current.signal,
          }
        );

        if (response?.status === 403) {
          logoutDispatch();
          triggerNotification({
            message: formatMessage({ id: "account.deleted.message" }),
            type: "error", 
          });
          return;
        }

        const reader = response?.body?.getReader();
        const decoder = new TextDecoder();

        setDone(false);
        let streamBuffer = '';
        const isPrivateChat = { current: false }; // Use a reference object
        let admin_team_deleted =  { current: false };;
        
        // For backwards compatibility with older non-event format
        let isJsonFormat = false;
        
        while (true) {
          const streamResult = await reader?.read();
          
          if (!streamResult?.value) {
            if (streamResult?.done) {
              setDone(true);
              controllerRef.current = null;
              
              // Handle chat history update logic
              if (addHistoryRef.current === '') {
                setTimeout(() => {
                  if (data.newChatBoolean && chatId.current && isPrivateChat.current === false) {
                    const ID = chatId.current;
                    setRememberChatTopicLoad(true);
                    getNewChatTopic(chatId.current, data.folder_id).then(() => {
                      setTimeout(() => AddHistoryMessage(ID), 100);
                    })
                      .catch((err: any) => console.error(err));

                    setTimeout(() => chatId.current = 0, 110);
                  }
                }, 2000);
              }
              
              if (admin_team_deleted.current === true) {
                setAdminAccountDeleted(true);
                switchTeam();
                reject('Team has been deleted by admin');
              }
              
              resolve({ ...streamResult, chatId: chatId.current });
              break;
            }
            continue;
          }
          
          const decodedChunk = decoder.decode(streamResult.value, { stream: !streamResult.done });
          streamBuffer += decodedChunk;
          if (streamBuffer.includes('event:') && !isJsonFormat) {
            // Process event-based stream using our new enum-based handler
            processEventStream(streamBuffer, completeData, admin_team_deleted.current, isPrivateChat);
            streamBuffer = ''; // Clear the buffer after processing
          }
        }
        
      } catch (error) {          
        console.error("Stream error:", error);
        reject(error);
      }
    });
  };
  
  return { done, setDone, generateImage, controllerRef };
};